import { useEffect } from 'react'
import req from '@sportninja/common/api/request'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { useIntegrationsReducer } from './useIntegrationsReducer'

/**
 * Interface for useDaySmartIntegration hook parameters
 */
interface DaySmartIntegrationParams {
  accountUid?: string
  setFormValue: (name: string, value: string) => void
  integrationModalRef: React.RefObject<any>
  fetchMe: () => void
  watch: any
  integrations?: {
    daysmart?: {
      is_connected: boolean
    }
    usahockey?: {
      is_connected: boolean
    }
  }
}

/**
 * Interface for useDaySmartIntegration hook return value
 */
interface DaySmartIntegrationReturn {
  // State values
  acceptedTerms: boolean
  isLoading: boolean
  isLoadingTest: boolean
  testSuccess: boolean
  testSubmitted: boolean
  apiKey: string
  apiKeyError: string
  secretKey: string
  secretKeyError: string
  error: string

  // Actions
  actions: ReturnType<typeof useIntegrationsReducer>['actions']

  // Functions
  onPressTest: () => Promise<void>
  onPressSubmit: () => Promise<void>
  setCustomErrors: (invalid_fields: any) => void
}

/**
 * Custom hook for managing DaySmart integration functionality
 *
 * This hook encapsulates all DaySmart-related logic including:
 * - Fetching DaySmart API keys from the backend
 * - Testing DaySmart authentication with provided credentials
 * - Submitting/saving DaySmart API keys
 * - Managing DaySmart-related state and form handling
 * - Handling form watching for real-time updates
 *
 * @param params - Configuration object containing account UID, form methods, and refs
 * @returns Object containing state values, actions, and utility functions for DaySmart integration
 *
 * @example
 * ```typescript
 * const daySmartIntegration = useDaySmartIntegration({
 *   accountUid: account?.uid,
 *   setFormValue,
 *   integrationModalRef,
 *   fetchMe,
 *   watch
 * })
 *
 * // Access state
 * const { apiKey, isLoading, testSuccess } = daySmartIntegration
 *
 * // Perform actions
 * await daySmartIntegration.onPressTest()
 * await daySmartIntegration.onPressSubmit()
 * ```
 */
export const useDaySmartIntegration = ({
  accountUid,
  setFormValue,
  integrationModalRef,
  fetchMe,
  watch,
  integrations,
}: DaySmartIntegrationParams): DaySmartIntegrationReturn => {
  console.log(
    '🔄 useDaySmartIntegration: Initializing DaySmart integration hook'
  )

  const { state, actions } = useIntegrationsReducer()
  const {
    acceptedTerms,
    isLoading,
    isLoadingTest,
    testSuccess,
    testSubmitted,
    apiKey,
    apiKeyError,
    secretKey,
    secretKeyError,
    error,
  } = state

  // Check if DaySmart is connected and fetch API keys if needed
  useEffect(() => {
    if (!accountUid) {
      return
    }

    // Set connection status from integrations data
    if (integrations?.daysmart?.is_connected) {
      actions.setAcceptedTerms(true)
      console.log('✅ useDaySmartIntegration: DaySmart is connected')
    }

    const fetchKeys = async () => {
      console.log('🔄 useDaySmartIntegration: Fetching DaySmart API keys')
      try {
        const result = await req(
          `/account/${accountUid}/integrations/daysmart/api-keys`
        )
        const { data } = result
        const { api_key, api_secret } = data
        if (!api_key || !api_secret) {
          console.log('⚠️ useDaySmartIntegration: No API keys found')
          return
        }
        setFormValue('daysmart_api_key', api_key)
        setFormValue('daysmart_secret_key', api_secret)
        actions.setDaySmartKeys(api_key, api_secret)
        console.log(
          '✅ useDaySmartIntegration: DaySmart API keys fetched successfully'
        )
      } catch (e) {
        const errorMessage = getErrorMessage(e)
        console.error(
          '🚨 useDaySmartIntegration: Failed to fetch API keys',
          errorMessage
        )
        actions.setError(errorMessage)
      }
    }

    fetchKeys()
    // Only depend on accountUid and integrations to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUid, integrations?.daysmart?.is_connected])

  // Watch form changes for real-time updates
  useEffect(() => {
    const unsubscribe = watch((value: any, { name }: { name: string }) => {
      if (name === 'daysmart_api_key') {
        actions.setApiKey(value?.daysmart_api_key || '')
      }
      if (name === 'daysmart_secret_key') {
        actions.setSecretKey(value?.daysmart_secret_key || '')
      }
    })

    // Ensure we return a proper cleanup function
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe()
      }
    }
  }, [watch, actions])

  /**
   * Sets custom field errors for DaySmart integration form
   * @param invalid_fields - Object containing field-specific error messages
   */
  const setCustomErrors = (invalid_fields: any) => {
    console.log(
      '🔄 useDaySmartIntegration: Setting custom field errors',
      invalid_fields
    )
    actions.setFieldErrors({
      apiKeyError: invalid_fields.api_key,
      secretKeyError: invalid_fields.api_secret,
    })
  }

  /**
   * Tests DaySmart authentication with current API credentials
   * Makes a POST request to validate the provided API key and secret
   */
  const onPressTest = async () => {
    console.log('🔄 useDaySmartIntegration: Testing DaySmart authentication')
    try {
      actions.setError('')
      actions.setIsLoadingTest(true)
      await req(
        `/account/${accountUid}/integrations/daysmart/authentication-test`,
        {
          method: 'POST',
          body: JSON.stringify({
            api_key: apiKey,
            api_secret: secretKey,
          }),
        }
      )
      actions.setTestSuccess(true)
      actions.setTestSubmitted(true)
      console.log('✅ useDaySmartIntegration: Authentication test successful')
    } catch (e: any) {
      console.error('🚨 useDaySmartIntegration: Authentication test failed', e)
      if (e?.invalid_fields) {
        setCustomErrors(e.invalid_fields)
        return
      }

      const errorMessage = getErrorMessage(e)
      actions.setError(errorMessage)
    } finally {
      actions.setIsLoadingTest(false)
    }
  }

  /**
   * Submits and saves DaySmart API credentials
   * Makes a POST request to store the validated API key and secret
   */
  const onPressSubmit = async () => {
    console.log(
      '🔄 useDaySmartIntegration: Submitting DaySmart API credentials'
    )
    try {
      actions.setError('')
      actions.setIsLoading(true)
      await req(`/account/${accountUid}/integrations/daysmart/api-keys`, {
        method: 'POST',
        body: JSON.stringify({
          api_key: apiKey,
          api_secret: secretKey,
        }),
      })
      fetchMe()
      integrationModalRef?.current?.closeModal()
      actions.resetTestState()
      console.log(
        '✅ useDaySmartIntegration: API credentials submitted successfully'
      )
    } catch (e) {
      const errorMessage = getErrorMessage(e)
      console.error(
        '🚨 useDaySmartIntegration: Failed to submit API credentials',
        errorMessage
      )
      actions.setError(errorMessage)
    } finally {
      actions.setIsLoading(false)
    }
  }

  return {
    // State values
    acceptedTerms,
    isLoading,
    isLoadingTest,
    testSuccess,
    testSubmitted,
    apiKey,
    apiKeyError,
    secretKey,
    secretKeyError,
    error,

    // Actions
    actions,

    // Functions
    onPressTest,
    onPressSubmit,
    setCustomErrors,
  }
}
